import {useEffect, useMemo, useState} from 'react'
import {useTable, Column, ColumnInstance, Row} from 'react-table'
import {useIntl} from 'react-intl'
import {useParams} from 'react-router-dom'
import {CustomHeaderColumn} from './columns/CustomHeaderColumn'
import {CustomRow} from './columns/CustomRow'
import {useQueryResponseLoading} from '../../core/QueryResponseProvider'
import {useListView} from '../../core/ListViewProvider'
import {useColumnVisibility} from '../../core/ColumnVisibilityProvider'
import {DropdownOpenProvider} from '../../../../../_metronic/context/DropdownOpenProvider'
import {tasksColumns} from './columns/_columns'
import {Task} from '../../core/_models'
import {KTCardBody} from '../../../../../_metronic/helpers'
import {getSubtasksByParentId} from '../../core/_requests'
import {TasksByUserListPagination} from '../pagination/TasksByUserListPagination'
import {Loading} from '../../../../../_metronic/partials/components/loading/Loading'
import {useIsMobile} from '../../../../../_metronic/hooks/useIsMobile'

const TasksByUserTable = () => {
  const {tasks} = useListView()
  const isLoading = useQueryResponseLoading()
  const {hideDatesColumn} = useColumnVisibility()
  const isMobile = useIsMobile()
  const {userId} = useParams()
  const [expandedRows, setExpandedRows] = useState<Set<number>>(new Set())
  const [subtasksMap, setSubtasksMap] = useState<Map<number, Task[]>>(new Map())
  const [loadingSubtasks, setLoadingSubtasks] = useState<Set<number>>(new Set())

  const columns = useMemo<Column<Task>[]>(() => {
    if (!isMobile) {
      return tasksColumns as Column<Task>[]
    }

    const mobileOrder: Array<Column<Task>['id']> = ['id', 'name', 'timetracking', 'actions']

    return mobileOrder
      .map((id) => tasksColumns.find((col) => col.id === id))
      .filter((col): col is Column<Task> => Boolean(col))
  }, [isMobile])

  const intl = useIntl()

  // Tarefas da API: level 0, exceto se o pai estiver expandido (aí só aparecem no dropdown com recuo).
  // Dropdown: subtarefas de subtasksMap com level 1 só quando o pai está expandido.
  const data = useMemo(() => {
    const taskList = Array.isArray(tasks) ? tasks : []
    const result: Array<Task & {level?: number}> = []
    for (const task of taskList) {
      const parentId = task.parent_id != null && String(task.parent_id) !== '' ? Number(task.parent_id) : null
      const parentIsExpanded = parentId != null && expandedRows.has(parentId)
      if (parentIsExpanded) continue // mostrada só sob o pai no dropdown, evita duplicata
      result.push({...task, level: 0})
      const taskIdNum = Number(task.id)
      if (taskIdNum && expandedRows.has(taskIdNum) && subtasksMap.has(taskIdNum)) {
        const subtasks = subtasksMap.get(taskIdNum)!
        for (const st of subtasks) {
          result.push({...st, level: 1})
        }
      }
    }
    return result
  }, [tasks, expandedRows, subtasksMap])

  const {getTableProps, getTableBodyProps, headers, rows, prepareRow, toggleHideColumn} = useTable({
    columns,
    data,
  })

  // Ocultar/mostrar coluna de datas quando o estado mudar
  useEffect(() => {
    toggleHideColumn('dates', hideDatesColumn)
  }, [hideDatesColumn, toggleHideColumn])

  const handleToggleExpand = (taskId: number) => {
    const id = Number(taskId)
    if (expandedRows.has(id)) {
      setExpandedRows((prev) => {
        const next = new Set(prev)
        next.delete(id)
        return next
      })
    } else {
      if (subtasksMap.has(id) || loadingSubtasks.has(id) || !userId) {
        setExpandedRows((prev) => new Set(prev).add(id))
      } else {
        setLoadingSubtasks((prev) => new Set(prev).add(id))
        getSubtasksByParentId(id, parseInt(userId))
          .then((response) => {
            const subtasks = response?.data && Array.isArray(response.data) ? response.data : []
            setSubtasksMap((prev) => {
              const newMap = new Map(prev)
              newMap.set(id, subtasks)
              return newMap
            })
            setExpandedRows((prev) => {
              const next = new Set(prev)
              next.add(id)
              return next
            })
          })
          .catch((error) => console.error('Error loading subtasks:', error))
          .finally(() => {
            setLoadingSubtasks((prev) => {
              const newSet = new Set(prev)
              newSet.delete(id)
              return newSet
            })
          })
      }
    }
  }

  return (
    <DropdownOpenProvider>
      <div id='kt_tasks_project_list_pane' className='tab-pane fade show active'>
        <KTCardBody className='py-4 px-0'>
          <div className='table-responsive'>
            <table
              id='kt_table_tasks'
              className='table align-middle table-row-dashed fs-6 gy-1 dataTable no-footer tab-pane fade show active'
              {...getTableProps()}
            >
              {rows.length > 0 && (
                <thead>
                  <tr className='text-start text-muted fw-bolder fs-7 gs-0 border-bottom-0'>
                    {headers
                      .filter((column: ColumnInstance<Task>) => column.isVisible)
                      .map((column: ColumnInstance<Task>) => (
                        <CustomHeaderColumn key={column.id} column={column} />
                      ))}
                  </tr>
                </thead>
              )}
              <tbody className='text-gray-600' {...getTableBodyProps()}>
                {rows.length > 0 ? (
                  rows.map((row: Row<Task>, i) => {
                    prepareRow(row)
                    const task = row.original as Task & {level?: number}
                    const isSubtask = task.parent_id !== null && task.parent_id !== undefined
                    const isParent = task.subtasks_count !== undefined && task.subtasks_count !== null && task.subtasks_count > 0
                    const taskId = task.id ? (typeof task.id === 'string' ? parseInt(task.id) : task.id) : undefined
                    const isExpanded = taskId ? expandedRows.has(taskId) : false
                    const isLoading = taskId ? loadingSubtasks.has(taskId) : false
                    const level = task.level ?? 0

                    return (
                      <CustomRow
                        row={row}
                        key={row.original.id}
                        isSubtask={isSubtask}
                        isParent={isParent}
                        isExpanded={isExpanded}
                        isLoading={isLoading}
                        onToggleExpand={handleToggleExpand}
                        columns={columns}
                        level={level}
                        // Sticky causa sobreposição real com a coluna Status em larguras
                        // intermediárias (ex. ~1500px), não só no mobile — desativado sempre.
                        disableStickyActions
                      />
                    )
                  })
                ) : (
                  <tr>
                    <td colSpan={headers.filter((column: ColumnInstance<Task>) => column.isVisible).length}>
                      <div className='d-flex flex-column align-items-center justify-content-center text-center py-15'>
                        <i className='bi bi-search text-muted mb-3 tbu-empty-icon'></i>
                        <span className='fs-5 fw-semibold text-gray-700'>
                          {intl.formatMessage({id: 'TABLE.GENERAL.NO_RECORD_FOUND'})}
                        </span>
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
        </div>
        <TasksByUserListPagination />

        {isLoading && <Loading />}
        </KTCardBody>
      </div>
    </DropdownOpenProvider>
  )
}

export {TasksByUserTable}
